import { getStateBounds } from '@/lib/locationUtils';

// Define types for state bounds
interface StateBounds {
  north: number;
  south: number;
  west: number;
  east: number;
  center: { lat: number; lng: number };
}

interface StateBoundsConfig {
  [stateName: string]: StateBounds;
}

/**
 * Get state bounds dynamically from database
 * Falls back to hardcoded bounds if database is unavailable
 */
export const getStateBoundsForSearch = async (stateName: string): Promise<StateBounds | null> => {
  try {
    // Try to get bounds from database first
    const dbBounds = await getStateBounds(stateName);
    if (dbBounds) {
      return {
        north: dbBounds.north,
        south: dbBounds.south,
        west: dbBounds.west,
        east: dbBounds.east,
        center: { lat: (dbBounds.north + dbBounds.south) / 2, lng: (dbBounds.west + dbBounds.east) / 2 }
      };
    }
    
    // Fallback to hardcoded bounds
    const hardcodedBounds = GOOGLE_MAPS_CONFIG.STATE_BOUNDS[stateName];
    return hardcodedBounds || null;
  } catch (error) {
    console.error('Error getting state bounds:', error);
    // Fallback to hardcoded bounds
    const hardcodedBounds = GOOGLE_MAPS_CONFIG.STATE_BOUNDS[stateName];
    return hardcodedBounds || null;
  }
};

export const GOOGLE_MAPS_CONFIG = {
  // Default center coordinates (Nigeria)
  DEFAULT_CENTER: {
    lat: 9.0820,
    lng: 8.6753
  },
  // Default search radius in meters (50km)
  DEFAULT_RADIUS: 50000,
  // Default zoom level for maps
  DEFAULT_ZOOM: 6,
  // Restrict results to Nigeria
  DEFAULT_BOUNDS: {
    north: 13.8856,
    south: 4.2773,
    west: 2.6684,
    east: 14.6801
  },
  // State-specific bounds for restricting searches
  STATE_BOUNDS: {
    'Lagos': {
      north: 6.7027,
      south: 6.3833,
      west: 2.8167,
      east: 3.6833,
      center: { lat: 6.5244, lng: 3.3792 }
    },
    'Oyo': {
      north: 9.1333,
      south: 7.3833,
      west: 2.9167,
      east: 4.6167,
      center: { lat: 8.0000, lng: 3.9333 }
    }
  } as StateBoundsConfig,
  // API options for location selection
  PLACES_OPTIONS: {
    componentRestrictions: { country: 'ng' },
    // Allow broader search but maintain privacy in what we store
    types: [
      // Area level types (these will be stored)
      'locality',
      'sublocality',
      'neighborhood',
      // Street level types (these can be searched but won't be stored)
      'route',
      'street_address'
    ]
  },
  // SECURITY: Additional restrictions for enhanced privacy
  SECURITY_CONFIG: {
    // Types that are allowed to be stored
    ALLOWED_STORAGE_TYPES: [
      'locality',
      'sublocality',
      'sublocality_level_1',
      'sublocality_level_2',
      'sublocality_level_3',
      'neighborhood',
      'administrative_area_level_2', // LGA level
      'administrative_area_level_3', // Sub-LGA level
      'postal_town',
      'colloquial_area'
    ],
    // Types that can be searched but won't be stored
    SEARCHABLE_TYPES: [
      'route',
      'street_address',
      'locality',
      'sublocality',
      'sublocality_level_1',
      'sublocality_level_2',
      'sublocality_level_3',
      'neighborhood',
      'administrative_area_level_1', // State level
      'administrative_area_level_2', // LGA level
      'administrative_area_level_3', // Sub-LGA level
      'postal_town',
      'colloquial_area'
    ],
    // Types that are completely excluded from both search and storage
    EXCLUDED_TYPES: [
      'street_number',
      'premise',
      'subpremise',
      'postal_code',
      'intersection',
      'plus_code',
      // Business and specific place types that could compromise privacy
      'accounting',
      'airport',
      'amusement_park',
      'aquarium',
      'art_gallery',
      'atm',
      'bakery',
      'bank',
      'bar',
      'beauty_salon',
      'bicycle_store',
      'book_store',
      'bowling_alley',
      'bus_station',
      'cafe',
      'campground',
      'car_dealer',
      'car_rental',
      'car_repair',
      'car_wash',
      'casino',
      'cemetery',
      'church',
      'city_hall',
      'clothing_store',
      'convenience_store',
      'courthouse',
      'dentist',
      'department_store',
      'doctor',
      'drugstore',
      'electrician',
      'electronics_store',
      'embassy',
      'fire_station',
      'florist',
      'funeral_home',
      'furniture_store',
      'gas_station',
      'gym',
      'hair_care',
      'hardware_store',
      'hindu_temple',
      'home_goods_store',
      'hospital',
      'insurance_agency',
      'jewelry_store',
      'laundry',
      'lawyer',
      'library',
      'light_rail_station',
      'liquor_store',
      'local_government_office',
      'locksmith',
      'lodging',
      'meal_delivery',
      'meal_takeaway',
      'mosque',
      'movie_rental',
      'movie_theater',
      'moving_company',
      'museum',
      'night_club',
      'painter',
      'park',
      'parking',
      'pet_store',
      'pharmacy',
      'physiotherapist',
      'plumber',
      'police',
      'post_office',
      'primary_school',
      'real_estate_agency',
      'restaurant',
      'roofing_contractor',
      'rv_park',
      'school',
      'secondary_school',
      'shoe_store',
      'shopping_mall',
      'spa',
      'stadium',
      'storage',
      'store',
      'subway_station',
      'supermarket',
      'synagogue',
      'taxi_stand',
      'tourist_attraction',
      'train_station',
      'transit_station',
      'travel_agency',
      'university',
      'veterinary_care',
      'zoo',
      // Points of interest that could be too specific
      'point_of_interest',
      'establishment'
    ],
    // User messages
    MESSAGES: {
      SEARCH_HELP: "You can search for streets to help find your area, but only the neighborhood will be saved.",
      PRIVACY_NOTICE: "For privacy and security, only your neighborhood or area name will be saved.",
      LOCATION_SELECTED: (streetName: string, areaName: string) => 
        `You searched for "${streetName}". We'll save this as "${areaName}" for privacy.`
    },
    // Maximum precision level allowed (neighborhood = 3, street = 5, house = 7)
    MAX_PRECISION_LEVEL: 3,
    // Minimum radius for location searches
    MIN_SEARCH_RADIUS: 1000 // 1km minimum
  }
}; 